import { Injectable } from '@angular/core';

const SKY_UI_ICON_GLOBAL_SVG_ID = 'sky-ui-icon-list';

interface Icon {
  content: string;
  symbolAdded: boolean;
}

@Injectable({ providedIn: 'root' })
export class SkyUiIconService {
  private globalSvgElement: SVGElement;
  private icons = new Map<string, Icon>();

  constructor() {
    this.initGlobalSvgElement();
  }

  add(name: string, content: string): void {
    // ignore same name icons
    if (this.icons.has(name)) {
      return;
    }

    this.icons.set(name, { content, symbolAdded: false });
  }

  createIconSymbol(name: string): void {
    const icon = this.icons.get(name);

    if (icon && icon.symbolAdded) {
      return;
    } else if (!icon) {
      console.error(`SkyUiIconService: icon with name '${name}' is not registered`);
      return;
    }

    // cand search for `#smth/smth` id, should be `#smth\\/smth`
    const symbolElementSelector = `#${name.replace(/\//g, '\\/')}`;
    // dont override already created icon symbol (e.x. when loading icons both from widgets and platrofm)
    const alreadyAddedIconElement = this.globalSvgElement.querySelector(symbolElementSelector);

    if (!alreadyAddedIconElement) {
      const symbolElement = this.createIconSymbolElement(name, icon.content);

      this.globalSvgElement.appendChild(symbolElement);
    }

    this.icons.set(name, { ...icon, symbolAdded: true });
  }

  /**
   * @internal
   * For storybook only
   */
  getKnownIcons(): Map<string, Icon> {
    return this.icons;
  }

  private initGlobalSvgElement(): void {
    this.globalSvgElement =
      document.querySelector<SVGElement>(`#${SKY_UI_ICON_GLOBAL_SVG_ID}`) ?? this.createGlobalSvgElement();
  }

  private createGlobalSvgElement(): SVGElement {
    const svgElement = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

    svgElement.setAttributeNS('http://www.w3.org/2000/xmlns/', 'xmlns:xlink', 'http://www.w3.org/1999/xlink');
    svgElement.setAttribute('id', SKY_UI_ICON_GLOBAL_SVG_ID);

    /* eslint-disable functional/immutable-data */
    svgElement.style.position = 'absolute';
    svgElement.style.width = '0';
    svgElement.style.height = '0';
    /* eslint-enable functional/immutable-data */

    document.body.appendChild(svgElement);

    return svgElement;
  }

  private createIconSymbolElement(name: string, content: string): SVGSymbolElement {
    const symbolElementHTML = content.replace('<svg', '<symbol').replace('</svg>', '</symbol>');

    // cant directly create symbol element
    const divElement = document.createElement('div');
    // eslint-disable-next-line functional/immutable-data
    divElement.innerHTML = `<svg>${symbolElementHTML}</svg>`;
    const symbolElement = divElement.querySelector('symbol')!;

    symbolElement.setAttribute('id', name);

    symbolElement.removeAttribute('xmlns');
    symbolElement.removeAttribute('xmlns:xlink');
    // remove original size, will use size from parent element
    symbolElement.removeAttribute('width');
    symbolElement.removeAttribute('height');

    return symbolElement;
  }
}
